<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useAlert } from 'dashboard/composables'
import Modal from 'dashboard/components/Modal.vue'
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue'
import Select from 'dashboard/components/crm/Select.vue'
import SearchableContactSelect from 'dashboard/components/crm/SearchableContactSelect.vue'
import ContactAPI from 'dashboard/api/contacts'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  activity: { type: Object, default: null },
  prefillContactId: { type: Number, default: null },
  prefillConversationId: { type: Number, default: null },
  lockAssociations: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'saved'])

const { t } = useI18n()
const store = useStore()
const isEditing = computed(() => !!props.activity)

const show = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

const typeOptions = computed(() =>
  ['task', 'call', 'meeting', 'email', 'note'].map(v => ({
    value: v,
    label: t(`TASKS.FORM.TYPE_OPTIONS.${v}`),
  }))
)
const priorityOptions = computed(() =>
  ['low', 'medium', 'high', 'urgent'].map(v => ({
    value: v,
    label: t(`TASKS.FORM.PRIORITY_OPTIONS.${v}`),
  }))
)
const statusOptions = computed(() =>
  ['pending', 'in_progress', 'done', 'cancelled'].map(v => ({
    value: v,
    label: t(`TASKS.FORM.STATUS_OPTIONS.${v}`),
  }))
)

const labelFor = (options, value) =>
  options.find(o => o.value === value)?.label ?? ''

// --- Recordatorio (presets relativos a due_at) ---
// Minutos de antelación por preset; 'none'/'custom' quedan fuera a propósito.
const REMINDER_OFFSETS = { '15m': 15, '30m': 30, '1h': 60, '1d': 1440 }

const reminderOptions = computed(() =>
  ['none', '15m', '30m', '1h', '1d', 'custom'].map(v => ({
    value: v,
    label: t(`TASKS.FORM.REMINDER_OPTIONS.${v}`),
  }))
)

const reminderPreset = ref('none')

const toLocalInput = date => {
  const pad = n => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

// Conversiones timezone-aware entre el instante ISO del backend y el
// string local del <input datetime-local>.
const isoToLocalInput = iso => (iso ? toLocalInput(new Date(iso)) : '')
const localInputToISO = local => (local ? new Date(local).toISOString() : null)

// due_at menos la antelación del preset → string datetime-local
const reminderFromPreset = (preset, dueAt) => {
  if (!dueAt || !(preset in REMINDER_OFFSETS)) return ''
  const d = new Date(dueAt)
  d.setMinutes(d.getMinutes() - REMINDER_OFFSETS[preset])
  return toLocalInput(d)
}

// Deduce el preset a partir de la diferencia due_at - reminder_at (para edición)
const presetFromForm = (reminderAt, dueAt) => {
  if (!reminderAt) return 'none'
  if (!dueAt) return 'custom'
  const diffMin = Math.round((new Date(dueAt) - new Date(reminderAt)) / 60000)
  const match = Object.entries(REMINDER_OFFSETS).find(([, m]) => m === diffMin)
  return match ? match[0] : 'custom'
}

function handleReminderPreset(preset) {
  reminderPreset.value = preset
  if (preset === 'none') {
    form.value.reminder_at = ''
  } else if (preset === 'custom') {
    if (!form.value.reminder_at) form.value.reminder_at = form.value.due_at || ''
  } else {
    form.value.reminder_at = reminderFromPreset(preset, form.value.due_at)
  }
}

// --- Contacto ---
// v-model que al cambiar el contacto limpia la conversación y recarga la lista
const contactIdModel = computed({
  get: () => form.value.contact_id,
  set: (newId) => {
    if (newId === form.value.contact_id) return
    form.value.contact_id = newId
    form.value.conversation_id = null
    fetchConversations(newId)
  },
})

// --- Conversación ---
const conversationResults = ref([])

const conversationOptions = computed(() => [
  { id: null, name: t('TASKS.FORM.CONVERSATION_PLACEHOLDER') },
  ...conversationResults.value.map(c => ({ id: c.id, name: `#${c.id}` })),
])

async function fetchConversations(contactId) {
  conversationResults.value = []
  if (!contactId) return
  try {
    const { data: { payload } } = await ContactAPI.getConversations(contactId)
    conversationResults.value = payload || []
  } catch {
    conversationResults.value = []
  }
}

const defaultForm = () => ({
  activity_type: 'task',
  subject: '',
  description: '',
  due_at: '',
  reminder_at: '',
  priority: 'low',
  status: 'pending',
  owner_id: null,
  contact_id: null,
  conversation_id: null,
})

const form = ref(defaultForm())

watch(() => props.activity, (activity) => {
  conversationResults.value = []
  if (activity) {
    form.value = {
      activity_type: activity.activity_type,
      subject: activity.subject,
      description: activity.description || '',
      due_at: isoToLocalInput(activity.due_at),
      reminder_at: isoToLocalInput(activity.reminder_at),
      priority: activity.priority || 'low',
      status: activity.status || 'pending',
      owner_id: activity.owner_id,
      contact_id: activity.contact_id,
      conversation_id: activity.conversation_id,
    }
    if (activity.contact_id) fetchConversations(activity.contact_id)
  } else {
    form.value = defaultForm()
  }
  reminderPreset.value = presetFromForm(form.value.reminder_at, form.value.due_at)
}, { immediate: true })

watch(() => props.modelValue, (isOpen) => {
  if (isOpen && !props.activity) {
    conversationResults.value = []
    form.value = defaultForm()
    form.value.contact_id = props.prefillContactId
    form.value.conversation_id = props.prefillConversationId
    form.value.owner_id = store.getters['auth/getCurrentUser']?.id
    reminderPreset.value = 'none'
    if (props.prefillContactId) fetchConversations(props.prefillContactId)
  }
})

// Al cambiar el vencimiento, recomputar recordatorio si el preset es relativo
watch(() => form.value.due_at, due => {
  if (reminderPreset.value in REMINDER_OFFSETS) {
    form.value.reminder_at = reminderFromPreset(reminderPreset.value, due)
  }
})

const isSaving = computed(() => {
  const flags = store.getters['crmActivities/uiFlags']
  return flags.isCreating || flags.isUpdating
})

async function handleSubmit() {
  if (!form.value.subject.trim()) {
    useAlert(t('TASKS.ALERTS.SUBJECT_REQUIRED'))
    return
  }
  if (!form.value.due_at) {
    useAlert(t('TASKS.ALERTS.DUE_AT_REQUIRED'))
    return
  }
  try {
    const payload = {
      ...form.value,
      due_at: localInputToISO(form.value.due_at),
      reminder_at: localInputToISO(form.value.reminder_at),
    }
    if (isEditing.value) {
      await store.dispatch('crmActivities/update', { id: props.activity.id, ...payload })
      useAlert(t('TASKS.ALERTS.UPDATE_SUCCESS'))
    } else {
      await store.dispatch('crmActivities/create', payload)
      useAlert(t('TASKS.ALERTS.CREATE_SUCCESS'))
    }
    emit('saved')
    emit('update:modelValue', false)
  } catch {
    useAlert(t('TASKS.ALERTS.SAVE_ERROR'))
  }
}

function handleClose() {
  emit('update:modelValue', false)
}
</script>

<template>
  <Modal v-model:show="show" @close="handleClose">
    <div class="w-full px-8 py-6 flex flex-col gap-5">
      <h3 class="text-lg font-semibold text-n-slate-12">
        {{ isEditing ? $t('TASKS.FORM.TITLE_EDIT') : $t('TASKS.FORM.TITLE_CREATE') }}
      </h3>

      <div class="flex flex-col gap-4">
        <!-- Asunto -->
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('TASKS.FORM.SUBJECT_LABEL') }} <span class="text-n-ruby-10">*</span>
          </label>
          <input
            v-model="form.subject"
            type="text"
            maxlength="100"
            :placeholder="$t('TASKS.FORM.SUBJECT_PLACEHOLDER')"
            class="w-full px-3 py-2 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand outline-none transition-all bg-white dark:bg-n-solid-2 text-n-slate-12"
          />
        </div>

        <!-- Contacto + Conversación -->
        <div class="grid grid-cols-2 gap-3">
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">{{ $t('TASKS.FORM.CONTACT_LABEL') }}</label>
            <SearchableContactSelect
              v-model="contactIdModel"
              :disabled="lockAssociations"
              :placeholder="$t('TASKS.FORM.CONTACT_PLACEHOLDER')"
            />
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">{{ $t('TASKS.FORM.CONVERSATION_LABEL') }}</label>
            <Select
              v-model="form.conversation_id"
              class="conversation-select"
              :class="{ 'conversation-select-tall': form.contact_id }"
              :options="conversationOptions"
              option-value="id"
              option-label="name"
              :placeholder="$t('TASKS.FORM.CONVERSATION_PLACEHOLDER')"
              :disabled="lockAssociations"
            />
          </div>
        </div>

        <!-- Tipo + Prioridad + Estado -->
        <div class="grid grid-cols-3 gap-3">
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">{{ $t('TASKS.FORM.TYPE_LABEL') }}</label>
            <SelectMenu
              :options="typeOptions"
              :model-value="form.activity_type"
              :label="labelFor(typeOptions, form.activity_type)"
              sub-menu-position="bottom"
              @update:model-value="form.activity_type = $event"
            />
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">{{ $t('TASKS.FORM.PRIORITY_LABEL') }}</label>
            <SelectMenu
              :options="priorityOptions"
              :model-value="form.priority"
              :label="labelFor(priorityOptions, form.priority)"
              sub-menu-position="bottom"
              @update:model-value="form.priority = $event"
            />
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">{{ $t('TASKS.FORM.STATUS_LABEL') }}</label>
            <SelectMenu
              :options="statusOptions"
              :model-value="form.status"
              :label="labelFor(statusOptions, form.status)"
              sub-menu-position="bottom"
              @update:model-value="form.status = $event"
            />
          </div>
        </div>

        <!-- Descripción -->
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-12">{{ $t('TASKS.FORM.DESCRIPTION_LABEL') }}</label>
          <textarea
            v-model="form.description"
            rows="3"
            maxlength="500"
            :placeholder="$t('TASKS.FORM.DESCRIPTION_PLACEHOLDER')"
            class="w-full px-3 py-2 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand outline-none transition-all bg-white dark:bg-n-solid-2 text-n-slate-12 resize-none"
          />
        </div>

        <!-- Fechas -->
        <div class="grid grid-cols-2 gap-3">
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">
              {{ $t('TASKS.FORM.DUE_AT_LABEL') }} <span class="text-n-ruby-10">*</span>
            </label>
            <input
              v-model="form.due_at"
              type="datetime-local"
              class="w-full px-3 py-2 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand outline-none transition-all bg-white dark:bg-n-solid-2 text-n-slate-12"
            />
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">{{ $t('TASKS.FORM.REMINDER_AT_LABEL') }}</label>
            <SelectMenu
              :options="reminderOptions"
              :model-value="reminderPreset"
              :label="labelFor(reminderOptions, reminderPreset)"
              sub-menu-position="top"
              @update:model-value="handleReminderPreset"
            />
            <input
              v-if="reminderPreset === 'custom'"
              v-model="form.reminder_at"
              type="datetime-local"
              class="w-full px-3 py-2 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand outline-none transition-all bg-white dark:bg-n-solid-2 text-n-slate-12"
            />
          </div>
        </div>

        <!-- Botones -->
        <div class="flex justify-end gap-3 pt-1">
          <button
            type="button"
            :disabled="isSaving"
            class="px-4 py-2 bg-n-slate-9/10 hover:bg-n-slate-9/20 text-n-slate-12 rounded-lg cursor-pointer border-0 outline-none transition-all"
            @click="handleClose"
          >
            {{ $t('TASKS.FORM.CANCEL') }}
          </button>
          <button
            type="button"
            :disabled="isSaving"
            class="px-4 py-2 bg-n-brand hover:brightness-110 text-white rounded-lg cursor-pointer border-0 outline-none transition-all flex items-center gap-2 disabled:opacity-50"
            @click="handleSubmit"
          >
            <div v-if="isSaving" class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            {{ isEditing ? $t('TASKS.FORM.SAVE') : $t('TASKS.FORM.CREATE') }}
          </button>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style scoped>
/* Alturas exactas del SearchableContactSelect:
   - vacío: py-2 (16px) + borde (2px) + línea (24px) = 42px (2.625rem)
   - con contacto: py-2 (16px) + avatar h-8 (32px) + borde (2px) = 54px (3.375rem)
   El botón usa flex items-center, así que con height + padding vertical 0 el contenido queda centrado. */
.conversation-select :deep(button) {
  height: 2.625rem;
  padding-top: 0;
  padding-bottom: 0;
}

.conversation-select-tall :deep(button) {
  height: 3.375rem;
}
</style>
